import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
  Save as SaveIcon,
  ViewColumn as ViewColumnIcon,
} from "@mui/icons-material";
import SidebarLayout from "../../shared/sections/sidebar/SidebarLayout";
import { apiFetch } from "../../api/apiClient";
import { useAuth } from "../../context/AuthContext";

const API_BASE = "/api/admin";

const getBlankForm = (tower = "", commodity = "", component = "", targetField = "") => ({
  tower,
  commodity,
  component,
  source_category: "",
  target_field: targetField,
  allowed_values: "",
  min_value: "",
  max_value: "",
  guidance_text: "",
  is_active: true,
  field_description: "",
  selection_guidance: "",
  decision_inputs: "",
  fallback_guidance: "",
});

const mapItemToForm = (item) => ({
  tower: item.tower || "",
  commodity: item.commodity || "",
  component: item.component || "",
  source_category: item.source_category || "",
  target_field: item.target_field || "",
  allowed_values: item.allowed_values || "",
  min_value: item.min_value === null || item.min_value === undefined ? "" : String(item.min_value),
  max_value: item.max_value === null || item.max_value === undefined ? "" : String(item.max_value),
  guidance_text: item.guidance_text || "",
  is_active: !!item.is_active,
  field_description: item.field_description || "",
  selection_guidance: item.selection_guidance || "",
  decision_inputs: item.decision_inputs || "",
  fallback_guidance: item.fallback_guidance || "",
});

const toNumberOrNull = (value) => {
  if (value === null || value === undefined || String(value).trim() === "") {
    return null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

export default function ConstraintsMasterPage() {
  const { user } = useAuth();

  const [towers, setTowers] = useState([]);
  const [commodities, setCommodities] = useState([]);
  const [selectedTower, setSelectedTower] = useState("");
  const [selectedCommodity, setSelectedCommodity] = useState("");

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [bulkSavingKey, setBulkSavingKey] = useState("");
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [selectedComponent, setSelectedComponent] = useState("");
  const [selectedTargetField, setSelectedTargetField] = useState("");
  const [componentSearch, setComponentSearch] = useState("");
  const [targetSearch, setTargetSearch] = useState("");

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState("create");
  const [dialogCreateType, setDialogCreateType] = useState("category");
  const [dialogItemId, setDialogItemId] = useState(null);
  const [dialogForm, setDialogForm] = useState(getBlankForm());
  const [dialogBaseline, setDialogBaseline] = useState(getBlankForm());

  const normalizedRole = useMemo(() => {
    if (Array.isArray(user?.role) && user.role.length > 0) {
      return String(user.role[0]?.role_name || "")
        .trim()
        .toLowerCase();
    }
    return String(user?.role || "")
      .trim()
      .toLowerCase();
  }, [user]);

  const isSuperAdmin = normalizedRole === "super admin";

  const componentGroups = useMemo(() => {
    const counts = {};
    items.forEach((item) => {
      const key = item.component || "Unknown";
      if (!counts[key]) {
        counts[key] = { count: 0, activeCount: 0 };
      }
      counts[key].count += 1;
      if (item.is_active) {
        counts[key].activeCount += 1;
      }
    });
    return Object.entries(counts)
      .map(([component, summary]) => ({
        component,
        count: summary.count,
        activeCount: summary.activeCount,
      }))
      .sort((a, b) => a.component.localeCompare(b.component));
  }, [items]);

  const filteredComponentGroups = useMemo(() => {
    const searchValue = componentSearch.trim().toLowerCase();
    if (!searchValue) return componentGroups;
    return componentGroups.filter((group) => group.component.toLowerCase().includes(searchValue));
  }, [componentGroups, componentSearch]);

  const targetFieldGroups = useMemo(() => {
    if (!selectedComponent) return [];
    const counts = {};
    items
      .filter((item) => (item.component || "Unknown") === selectedComponent)
      .forEach((item) => {
        const key = item.target_field || "Unknown";
        if (!counts[key]) {
          counts[key] = { count: 0, activeCount: 0 };
        }
        counts[key].count += 1;
        if (item.is_active) {
          counts[key].activeCount += 1;
        }
      });
    return Object.entries(counts)
      .map(([targetField, summary]) => ({
        targetField,
        count: summary.count,
        activeCount: summary.activeCount,
      }))
      .sort((a, b) => a.targetField.localeCompare(b.targetField));
  }, [items, selectedComponent]);

  const filteredTargetFieldGroups = useMemo(() => {
    const searchValue = targetSearch.trim().toLowerCase();
    if (!searchValue) return targetFieldGroups;
    return targetFieldGroups.filter((group) => group.targetField.toLowerCase().includes(searchValue));
  }, [targetFieldGroups, targetSearch]);

  const tableRows = useMemo(() => {
    if (!selectedComponent || !selectedTargetField) return [];
    return items
      .filter(
        (item) =>
          (item.component || "Unknown") === selectedComponent &&
          (item.target_field || "Unknown") === selectedTargetField,
      )
      .sort((a, b) => {
        const categoryCompare = String(a.source_category || "").localeCompare(String(b.source_category || ""));
        if (categoryCompare !== 0) return categoryCompare;
        return Number(a.id || 0) - Number(b.id || 0);
      });
  }, [items, selectedComponent, selectedTargetField]);

  const selectedComponentRows = useMemo(() => {
    if (!selectedComponent) return [];
    return items.filter((item) => (item.component || "Unknown") === selectedComponent);
  }, [items, selectedComponent]);

  const selectedComponentActiveCount = useMemo(
    () => selectedComponentRows.filter((item) => !!item.is_active).length,
    [selectedComponentRows],
  );

  const selectedTargetActiveCount = useMemo(
    () => tableRows.filter((item) => !!item.is_active).length,
    [tableRows],
  );

  const dialogIsDirty = useMemo(
    () => JSON.stringify(dialogForm) !== JSON.stringify(dialogBaseline),
    [dialogForm, dialogBaseline],
  );

  useEffect(() => {
    if (!isSuperAdmin) return;
    fetchTowers();
  }, [isSuperAdmin]);

  useEffect(() => {
    if (!isSuperAdmin) return;
    if (!selectedTower) return;

    setSelectedCommodity("");
    setItems([]);
    setSelectedComponent("");
    setSelectedTargetField("");
    setComponentSearch("");
    setTargetSearch("");
    setDialogOpen(false);
    fetchCommodities(selectedTower);
  }, [selectedTower, isSuperAdmin]);

  useEffect(() => {
    if (!isSuperAdmin) return;
    if (!selectedTower || !selectedCommodity) return;
    fetchItems(selectedTower, selectedCommodity);
  }, [selectedTower, selectedCommodity, isSuperAdmin]);

  useEffect(() => {
    if (!selectedTower || !selectedCommodity) return;

    if (items.length === 0) {
      setSelectedComponent("");
      setSelectedTargetField("");
      return;
    }

    const selectedComponentExists =
      selectedComponent && items.some((item) => (item.component || "Unknown") === selectedComponent);

    if (!selectedComponentExists) {
      const firstComponent = items[0]?.component || "Unknown";
      setSelectedComponent(firstComponent);
      const firstTargetForComponent =
        items.find((item) => (item.component || "Unknown") === firstComponent)?.target_field || "";
      setSelectedTargetField(firstTargetForComponent);
      return;
    }

    const selectedTargetExists =
      selectedTargetField &&
      items.some(
        (item) =>
          (item.component || "Unknown") === selectedComponent &&
          (item.target_field || "Unknown") === selectedTargetField,
      );

    if (!selectedTargetExists) {
      const firstTargetForComponent =
        items.find((item) => (item.component || "Unknown") === selectedComponent)?.target_field || "";
      setSelectedTargetField(firstTargetForComponent);
    }
  }, [items, selectedTower, selectedCommodity, selectedComponent, selectedTargetField]);

  const fetchTowers = async () => {
    try {
      const response = await apiFetch(`${API_BASE}/towers`);
      const data = await response.json();
      setTowers(data.towers || []);
    } catch (fetchError) {
      setError(fetchError.message || "Failed to load towers");
    }
  };

  const fetchCommodities = async (tower) => {
    try {
      const response = await apiFetch(`${API_BASE}/commodities?tower=${encodeURIComponent(tower)}`);
      const data = await response.json();
      setCommodities(data.commodities || []);
    } catch (fetchError) {
      setError(fetchError.message || "Failed to load commodities");
    }
  };

  const fetchItems = async (tower, commodity) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ tower, commodity });
      const response = await apiFetch(`${API_BASE}/constraint-master?${params.toString()}`);
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || "Failed to load constraint master rows");
      }
      const list = data.items || [];
      setItems(list);
      setError(null);
      return list;
    } catch (fetchError) {
      setItems([]);
      setError(fetchError.message || "Failed to load constraint master rows");
      return [];
    } finally {
      setLoading(false);
    }
  };

  const handleComponentSelect = (componentName) => {
    setSelectedComponent(componentName);
    const firstTargetForComponent =
      items.find((item) => (item.component || "Unknown") === componentName)?.target_field || "";
    setSelectedTargetField(firstTargetForComponent);
  };

  const handleTargetSelect = (targetField) => {
    setSelectedTargetField(targetField);
  };

  const handleDialogFieldChange = (field, value) => {
    setDialogForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleOpenCreate = () => {
    if (!selectedTower || !selectedCommodity || !selectedComponent) {
      setError("Select tower, commodity, and component before adding a category");
      return;
    }

    const blank = getBlankForm(
      selectedTower,
      selectedCommodity,
      selectedComponent,
      selectedTargetField || "",
    );
    setDialogMode("create");
    setDialogCreateType("category");
    setDialogItemId(null);
    setDialogForm(blank);
    setDialogBaseline(blank);
    setDialogOpen(true);
    setError(null);
    setSuccess(null);
  };

  const handleOpenCreateTargetColumn = () => {
    if (!selectedTower || !selectedCommodity || !selectedComponent) {
      setError("Select tower, commodity, and component before adding a target column");
      return;
    }

    const blank = getBlankForm(selectedTower, selectedCommodity, selectedComponent, "");
    setDialogMode("create");
    setDialogCreateType("target");
    setDialogItemId(null);
    setDialogForm(blank);
    setDialogBaseline(blank);
    setDialogOpen(true);
    setError(null);
    setSuccess(null);
  };

  const handleOpenEdit = (row) => {
    const mapped = mapItemToForm(row);
    setDialogMode("edit");
    setDialogCreateType("category");
    setDialogItemId(row.id);
    setDialogForm(mapped);
    setDialogBaseline(mapped);
    setDialogOpen(true);
    setError(null);
    setSuccess(null);
  };

  const handleDialogClose = () => {
    if (saving) return;
    setDialogOpen(false);
  };

  const handleDialogSave = async () => {
    if (
      !dialogForm.tower ||
      !dialogForm.commodity ||
      !dialogForm.component ||
      !dialogForm.target_field ||
      !dialogForm.source_category
    ) {
      setError("Tower, commodity, component, target field, and source category are required");
      return;
    }

    const payload = {
      tower: dialogForm.tower,
      commodity: dialogForm.commodity,
      component: dialogForm.component,
      source_category: dialogForm.source_category,
      target_field: dialogForm.target_field,
      allowed_values: dialogForm.allowed_values,
      min_value: toNumberOrNull(dialogForm.min_value),
      max_value: toNumberOrNull(dialogForm.max_value),
      guidance_text: dialogForm.guidance_text,
      is_active: !!dialogForm.is_active,
      field_description: dialogForm.field_description,
      selection_guidance: dialogForm.selection_guidance,
      decision_inputs: dialogForm.decision_inputs,
      fallback_guidance: dialogForm.fallback_guidance,
    };

    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const url =
        dialogMode === "edit" && dialogItemId
          ? `${API_BASE}/constraint-master/${dialogItemId}`
          : `${API_BASE}/constraint-master`;
      const method = dialogMode === "edit" && dialogItemId ? "PUT" : "POST";

      const response = await apiFetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || "Failed to save constraint master item");
      }

      await fetchItems(selectedTower, selectedCommodity);
      setSelectedComponent(payload.component);
      setSelectedTargetField(payload.target_field);
      setDialogOpen(false);
      if (dialogMode === "edit") {
        setSuccess("Category updated successfully");
      } else {
        setSuccess(dialogCreateType === "target" ? "Target column created successfully" : "Category created successfully");
      }
    } catch (saveError) {
      setError(saveError.message || "Failed to save constraint master item");
    } finally {
      setSaving(false);
    }
  };

  const handleBulkStatusUpdate = async (scope, isActive, overrides = {}) => {
    const componentName = overrides.component || selectedComponent;
    const targetFieldName = overrides.targetField || selectedTargetField;

    if (!selectedTower || !selectedCommodity || !componentName) {
      setError("Select tower, commodity, and component first");
      return;
    }
    if (scope === "target" && !targetFieldName) {
      setError("Select a target column first");
      return;
    }

    const nextBulkSavingKey =
      scope === "target" ? `target:${componentName}:${targetFieldName}` : `component:${componentName}`;
    setBulkSavingKey(nextBulkSavingKey);
    setError(null);
    setSuccess(null);

    try {
      const response = await apiFetch(`${API_BASE}/constraint-master/bulk-status`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          tower: selectedTower,
          commodity: selectedCommodity,
          component: componentName,
          target_field: scope === "target" ? targetFieldName : undefined,
          scope,
          is_active: isActive,
        }),
      });

      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || "Failed to update status");
      }

      await fetchItems(selectedTower, selectedCommodity);
      setSuccess(
        scope === "target"
          ? `Target column "${targetFieldName}" marked ${isActive ? "active" : "inactive"}`
          : `Component "${componentName}" marked ${isActive ? "active" : "inactive"}`,
      );
    } catch (saveError) {
      setError(saveError.message || "Failed to update status");
    } finally {
      setBulkSavingKey("");
    }
  };

  if (!isSuperAdmin) {
    return (
      <SidebarLayout>
        <Box sx={{ px: 2, pt: 2 }}>
          <Alert severity="warning">This page is available to Super Admin only.</Alert>
        </Box>
      </SidebarLayout>
    );
  }

  return (
    <SidebarLayout>
      <Box sx={{ display: "flex", height: "100%", bgcolor: "background.default" }}>
        <Paper
          sx={{
            width: 360,
            borderRadius: 0,
            borderRight: 1,
            borderColor: "divider",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box sx={{ p: 1.5, borderBottom: 1, borderColor: "divider" }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Constraints Master
            </Typography>

            <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
              <InputLabel>Tower</InputLabel>
              <Select
                value={selectedTower}
                label="Tower"
                onChange={(e) => setSelectedTower(e.target.value)}
              >
                {towers.map((tower) => (
                  <MenuItem key={tower.tower_code} value={tower.tower_code}>
                    {tower.tower_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth size="small" disabled={!selectedTower}>
              <InputLabel>Commodity</InputLabel>
              <Select
                value={selectedCommodity}
                label="Commodity"
                onChange={(e) => setSelectedCommodity(e.target.value)}
              >
                {commodities.map((commodity) => (
                  <MenuItem key={commodity.commodity_code} value={commodity.commodity_code}>
                    {commodity.commodity_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ px: 1.5, py: 1, borderBottom: 1, borderColor: "divider" }}>
            <TextField
              size="small"
              fullWidth
              label="Search Component"
              value={componentSearch}
              onChange={(e) => setComponentSearch(e.target.value)}
            />
            <Typography variant="caption" sx={{ mt: 1, display: "block", fontWeight: 600, color: "text.secondary" }}>
              COMPONENTS ({filteredComponentGroups.length})
            </Typography>
          </Box>

          <Box sx={{ maxHeight: "35%", overflow: "auto", borderBottom: 1, borderColor: "divider" }}>
            <List dense sx={{ p: 0 }}>
              {filteredComponentGroups.map((group) => {
                const componentSwitchKey = `component:${group.component}`;
                const isFullyActive = group.count > 0 && group.activeCount === group.count;
                const isUpdating = bulkSavingKey === componentSwitchKey;

                return (
                <ListItem
                  key={group.component}
                  disablePadding
                  secondaryAction={
                    <Switch
                      edge="end"
                      size="small"
                      checked={isFullyActive}
                      disabled={loading || !!bulkSavingKey}
                      onClick={(event) => event.stopPropagation()}
                      onChange={(event) => {
                        event.stopPropagation();
                        void handleBulkStatusUpdate("component", event.target.checked, {
                          component: group.component,
                        });
                      }}
                      sx={{ transform: "scale(0.8)" }}
                    />
                  }
                >
                  <ListItemButton
                    sx={{ pr: 9 }}
                    selected={selectedComponent === group.component}
                    onClick={() => handleComponentSelect(group.component)}
                  >
                    <ListItemText
                      primary={group.component}
                      secondary={
                        isUpdating ? "Updating..." : `${group.activeCount}/${group.count} active`
                      }
                    />
                  </ListItemButton>
                </ListItem>
                );
              })}
            </List>
          </Box>

          <Box sx={{ px: 1.5, py: 1, borderBottom: 1, borderColor: "divider" }}>
            <TextField
              size="small"
              fullWidth
              label="Search Target Column"
              value={targetSearch}
              onChange={(e) => setTargetSearch(e.target.value)}
              disabled={!selectedComponent}
            />
            <Typography variant="caption" sx={{ mt: 1, display: "block", fontWeight: 600, color: "text.secondary" }}>
              TARGET COLUMNS ({filteredTargetFieldGroups.length})
            </Typography>
          </Box>

          <Box sx={{ flex: 1, overflow: "auto" }}>
            <List dense sx={{ p: 0 }}>
              {filteredTargetFieldGroups.map((group) => {
                const targetSwitchKey = `target:${selectedComponent}:${group.targetField}`;
                const isFullyActive = group.count > 0 && group.activeCount === group.count;
                const isUpdating = bulkSavingKey === targetSwitchKey;

                return (
                <ListItem
                  key={group.targetField}
                  disablePadding
                  secondaryAction={
                    <Switch
                      edge="end"
                      size="small"
                      checked={isFullyActive}
                      disabled={loading || !!bulkSavingKey}
                      onClick={(event) => event.stopPropagation()}
                      onChange={(event) => {
                        event.stopPropagation();
                        void handleBulkStatusUpdate("target", event.target.checked, {
                          component: selectedComponent,
                          targetField: group.targetField,
                        });
                      }}
                      sx={{ transform: "scale(0.8)" }}
                    />
                  }
                >
                  <ListItemButton
                    sx={{ pr: 9 }}
                    selected={selectedTargetField === group.targetField}
                    onClick={() => handleTargetSelect(group.targetField)}
                  >
                    <ListItemText
                      primary={group.targetField}
                      secondary={
                        isUpdating ? "Updating..." : `${group.activeCount}/${group.count} active`
                      }
                    />
                  </ListItemButton>
                </ListItem>
                );
              })}
            </List>
          </Box>
        </Paper>

        <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
          <Paper
            sx={{
              px: 2,
              py: 0.75,
              borderRadius: 0,
              borderBottom: 1,
              borderColor: "divider",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {selectedTower && selectedCommodity
                  ? `${selectedTower}/${selectedCommodity}${selectedComponent ? `/${selectedComponent}` : ""}${
                      selectedTargetField ? `/${selectedTargetField}` : ""
                    }`
                  : "Select Tower and Commodity"}
              </Typography>
              {selectedComponent && (
                <Typography variant="body2" color="text.secondary">
                  Component: {selectedComponentActiveCount}/{selectedComponentRows.length} active
                  {selectedTargetField ? `  |  Target: ${selectedTargetActiveCount}/${tableRows.length} active` : ""}
                </Typography>
              )}
            </Box>
            <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", justifyContent: "flex-end" }}>
              <Tooltip title="Refresh">
                <span>
                  <IconButton
                    size="small"
                    onClick={() => selectedTower && selectedCommodity && fetchItems(selectedTower, selectedCommodity)}
                    disabled={!selectedTower || !selectedCommodity || loading || !!bulkSavingKey}
                  >
                    <RefreshIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Add Category">
                <span>
                  <IconButton
                    size="small"
                    onClick={handleOpenCreate}
                    disabled={!selectedTower || !selectedCommodity || !selectedComponent || !!bulkSavingKey}
                    color="primary"
                  >
                    <AddIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Add Target Column">
                <span>
                  <IconButton
                    size="small"
                    onClick={handleOpenCreateTargetColumn}
                    disabled={!selectedTower || !selectedCommodity || !selectedComponent || !!bulkSavingKey}
                    color="primary"
                  >
                    <ViewColumnIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          </Paper>

          {loading ? (
            <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <CircularProgress size={48} />
            </Box>
          ) : !selectedTower || !selectedCommodity ? (
            <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", p: 2 }}>
              <Typography variant="body1" color="text.secondary">
                Select Tower and Commodity to load constraints.
              </Typography>
            </Box>
          ) : !selectedComponent ? (
            <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", p: 2 }}>
              <Typography variant="body1" color="text.secondary">
                Select a component in the left panel.
              </Typography>
            </Box>
          ) : !selectedTargetField ? (
            <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", p: 2 }}>
              <Typography variant="body1" color="text.secondary">
                Select a target column in the left panel.
              </Typography>
            </Box>
          ) : (
            <Box sx={{ p: 2, flex: 1, overflow: "auto" }}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                  Categories For {selectedTargetField}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Showing all source categories under the selected component and target column.
                </Typography>

                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>ID</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Source Category</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Allowed Values</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Min</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Max</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Guidance</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Active</TableCell>
                        <TableCell sx={{ fontWeight: 600, width: 72 }}>Edit</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {tableRows.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                            <Typography variant="body2" color="text.secondary">
                              No categories found for this target column.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        tableRows.map((row) => (
                          <TableRow key={row.id} hover>
                            <TableCell>{row.id}</TableCell>
                            <TableCell>{row.source_category || "-"}</TableCell>
                            <TableCell>
                              <Typography
                                variant="body2"
                                sx={{ maxWidth: 240, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                                title={row.allowed_values || ""}
                              >
                                {row.allowed_values || "-"}
                              </Typography>
                            </TableCell>
                            <TableCell>{row.min_value ?? "-"}</TableCell>
                            <TableCell>{row.max_value ?? "-"}</TableCell>
                            <TableCell>
                              <Typography
                                variant="body2"
                                sx={{ maxWidth: 280, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                                title={row.guidance_text || ""}
                              >
                                {row.guidance_text || "-"}
                              </Typography>
                            </TableCell>
                            <TableCell>{row.is_active ? "Yes" : "No"}</TableCell>
                            <TableCell>
                              <Tooltip title="Edit Category">
                                <IconButton size="small" onClick={() => handleOpenEdit(row)}>
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Box>
          )}
        </Box>
      </Box>

      <Dialog open={dialogOpen} onClose={handleDialogClose} maxWidth="md" fullWidth>
        <DialogTitle>
          {dialogMode === "edit" ? "Edit Category" : dialogCreateType === "target" ? "Add Target Column" : "Add Category"}
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2, mt: 0.5 }}>
            <TextField label="Tower" value={dialogForm.tower} disabled />
            <TextField label="Commodity" value={dialogForm.commodity} disabled />
            <TextField
              label="Component"
              value={dialogForm.component}
              onChange={(e) => handleDialogFieldChange("component", e.target.value)}
              required
            />
            <TextField
              label="Target Field"
              value={dialogForm.target_field}
              onChange={(e) => handleDialogFieldChange("target_field", e.target.value)}
              required
              helperText={
                dialogMode === "create" && dialogCreateType === "target" ? "Enter a new target column name." : undefined
              }
            />
            <TextField
              label="Source Category"
              value={dialogForm.source_category}
              onChange={(e) => handleDialogFieldChange("source_category", e.target.value)}
              required
              helperText={
                dialogMode === "create" && dialogCreateType === "target"
                  ? "Add the first category for this target column."
                  : undefined
              }
            />
            <FormControlLabel
              control={
                <Switch
                  checked={!!dialogForm.is_active}
                  onChange={(e) => handleDialogFieldChange("is_active", e.target.checked)}
                />
              }
              label="Is Active"
            />
            <TextField
              label="Allowed Values"
              value={dialogForm.allowed_values}
              onChange={(e) => handleDialogFieldChange("allowed_values", e.target.value)}
              multiline
              rows={2}
              sx={{ gridColumn: { xs: "1 / span 1", md: "1 / span 2" } }}
            />
            <TextField
              label="Min Value"
              value={dialogForm.min_value}
              onChange={(e) => handleDialogFieldChange("min_value", e.target.value)}
            />
            <TextField
              label="Max Value"
              value={dialogForm.max_value}
              onChange={(e) => handleDialogFieldChange("max_value", e.target.value)}
            />
            <TextField
              label="Guidance Text"
              value={dialogForm.guidance_text}
              onChange={(e) => handleDialogFieldChange("guidance_text", e.target.value)}
              multiline
              rows={2}
              sx={{ gridColumn: { xs: "1 / span 1", md: "1 / span 2" } }}
            />
            <TextField
              label="Field Description"
              value={dialogForm.field_description}
              onChange={(e) => handleDialogFieldChange("field_description", e.target.value)}
              multiline
              rows={2}
              sx={{ gridColumn: { xs: "1 / span 1", md: "1 / span 2" } }}
            />
            <TextField
              label="Selection Guidance"
              value={dialogForm.selection_guidance}
              onChange={(e) => handleDialogFieldChange("selection_guidance", e.target.value)}
              multiline
              rows={2}
              sx={{ gridColumn: { xs: "1 / span 1", md: "1 / span 2" } }}
            />
            <TextField
              label="Decision Inputs"
              value={dialogForm.decision_inputs}
              onChange={(e) => handleDialogFieldChange("decision_inputs", e.target.value)}
              multiline
              rows={2}
            />
            <TextField
              label="Fallback Guidance"
              value={dialogForm.fallback_guidance}
              onChange={(e) => handleDialogFieldChange("fallback_guidance", e.target.value)}
              multiline
              rows={2}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleDialogSave}
            disabled={saving || !dialogIsDirty}
          >
            {saving ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {error && (
        <Alert
          severity="error"
          onClose={() => setError(null)}
          sx={{ position: "fixed", right: 20, bottom: 20, zIndex: 9999, minWidth: 340 }}
        >
          {error}
        </Alert>
      )}
      {success && (
        <Alert
          severity="success"
          onClose={() => setSuccess(null)}
          sx={{ position: "fixed", right: 20, bottom: 20, zIndex: 9999, minWidth: 340 }}
        >
          {success}
        </Alert>
      )}
    </SidebarLayout>
  );
}
